import React, {memo, useContext} from 'react';

import {WidthItem} from 'src/components/width-item';
import {MAX_NUMBER_VALUE} from 'src/constants';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {Dropdown} from '@pb/kit-component-dropdown';
import {FormattedDate, Integer} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {LocaleContext} from '@pb/kit-context-locale';

import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';

type Props = {
    limitId: string,
    limitDictionary: DictionaryItem,
    onClearTemporaryLimit?: (code: string) => void,
    onClearCustomLimit: (code: string) => void,

    defaultValue: number,
    customValue?: number,
    tempValue?: number,
    untilDate?: string,

    labelExtra?: React.ReactNode,
};

export const LimitItemLabel = memo((props: Props) => {
    const {
        limitId,
        limitDictionary,
        onClearTemporaryLimit,
        onClearCustomLimit,

        defaultValue,
        customValue,
        tempValue,
        untilDate,

        labelExtra,
    } = props;

    const {getLocalDate} = useDateTime();
    const {locale} = useContext(LocaleContext);

    if (tempValue !== undefined && untilDate || customValue !== undefined) {
        return (
            <WidthItem width="100%">
                <Group justify="space-between">
                    <Group gap="xs" alignItems="center">
                        {limitDictionary.description}
                        {labelExtra}
                    </Group>
                    <Dropdown
                        hoverEnabled={true}
                        anchor={({anchorRef, getAnchorProps}) => (
                            <span ref={anchorRef} {...getAnchorProps()}>
                                <Group gap="xxs" alignItems="center">
                                    {customValue !== undefined && <Icon type={Icons.COGS}/>}
                                    {tempValue === undefined && customValue !== undefined && (
                                        <b>custom</b>
                                    )}
                                    {tempValue !== undefined && (
                                        <Group gap="xxs">
                                            <Icon type={Icons.CALENDAR}/>
                                            <b>until {FormattedDate.format({
                                                value: getLocalDate(untilDate),
                                                format: mapDateFormat(locale.dateFormat, 'WORD'),
                                            })}
                                            </b>
                                        </Group>
                                    )}
                                </Group>
                            </span>

                        )}>

                        <Group direction="v" gap="xs">
                            {(tempValue !== undefined && onClearTemporaryLimit) ? (
                                <Group gap="xs" alignItems="center" justify="start">
                                    <Icon type={Icons.CALENDAR}/>
                                    <span>Temporary:</span>
                                    <b>{tempValue === MAX_NUMBER_VALUE
                                        ? 'Unlimited'
                                        : Integer.format(tempValue, locale)}
                                    </b>
                                    <BasicIconButton
                                        title="Clear Temporary Limit"
                                        iconProps={{type: Icons.CLOSE}}
                                        onClick={() => onClearTemporaryLimit(limitId)}
                                    />
                                </Group>
                            ) : null}
                            {customValue !== undefined ? (
                                <Group gap="xs" alignItems="center" justify="start">
                                    <Icon type={Icons.COGS}/>
                                    <span>Custom:</span>
                                    <b>{customValue === MAX_NUMBER_VALUE
                                        ? 'Unlimited'
                                        : Integer.format(customValue, locale)}
                                    </b>
                                    <BasicIconButton
                                        title="Clear Custom Limit"
                                        iconProps={{type: Icons.CLOSE}}
                                        onClick={() => onClearCustomLimit(limitId)}
                                    />
                                </Group>
                            ) : null}
                            <div style={{paddingLeft: 16}}>
                                <Group gap="xs" justify="start">
                                    <span>Default:</span>
                                    <b>{defaultValue === MAX_NUMBER_VALUE
                                        ? 'Unlimited'
                                        : Integer.format(defaultValue, locale)}
                                    </b>
                                </Group>
                            </div>
                        </Group>
                    </Dropdown>
                </Group>
            </WidthItem>
        );
    }

    if (labelExtra) {
        return (
            <Group gap="xs" alignItems="center">
                <span>{limitDictionary.description}</span>
                {labelExtra}
            </Group>
        );
    }

    return <span>{limitDictionary.description}</span>;
});
